import { useMemo, useState } from "react";
import {
  AutocompleteInput,
  Box,
  Command,
  CommandGroup,
  CommandGroupLabel,
  CommandList,
  Separator,
  Text,
} from "@salestrak/ui";

import { OptionsPanelHeaderRung } from "#app/components/view-options/options-panel-slots";
import { AnalyticsContext, IssueEvent, useTrackSearch } from "#app/lib/analytics";
import type { QuickFilterLayerFor, UseFiltersResult } from "#app/lib/filters/types";
import { useQuickSearchEntities } from "#app/lib/hooks/use-quick-filter-directory";
import {
  searchKinds,
  searchOptions,
  type QuickFilterOption,
  type QuickFilterSection,
  QUICK_SEARCH_MATCH_CAP,
  writeQuickValue,
} from "./quick-inputs/quick-filter-options";
import { QuickOptionRow } from "./quick-inputs/quick-option-row";


type QuickFilterSearchProps = {
  sections: QuickFilterSection[];
  /** The layer each field lives in — see `QuickFilterLayerFor`. */
  layerFor: QuickFilterLayerFor;
  /** Mirrors the query up so the panel can swap sections for results. */
  onQueryChange: (query: string) => void;
};

type Match = {
  section: QuickFilterSection;
  options: QuickFilterOption[];
  /** Matches beyond {@link QUICK_SEARCH_MATCH_CAP}. */
  hidden: number;
};

/**
 * The panel's ONE search field: it searches the VALUES of every quick filter
 * at once — type a member's name and it offers them under Assignee, type
 * "chase" and it offers the team under Assignee and the stage under Workflow.
 *
 * This is the route in for a reader who knows the VALUE but not which filter
 * holds it. Each filter's own list searches its own pool, one drill down; this
 * field is what saves opening seven of them to find out which one has it.
 *
 * Results toggle in place and the query survives the press, so several values
 * across several filters go on in one pass. Escape (or the ×) drops back to
 * the browsable rows.
 */
export function QuickFilterSearch({
  sections,
  layerFor,
  onQueryChange,
}: QuickFilterSearchProps) {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();

  const kinds = useMemo(
    () => searchKinds(sections.map((section) => section.def.type)),
    [sections],
  );
  const server = useQuickSearchEntities(trimmed, kinds);

  const results = useMemo<Match[]>(() => {
    if (!trimmed) return [];
    return sections.flatMap((section) => {
      const hits = searchOptions(section.options, section.def.type, trimmed, server);
      if (hits.length === 0) return [];
      return [
        {
          section,
          options: hits.slice(0, QUICK_SEARCH_MATCH_CAP),
          hidden: Math.max(0, hits.length - QUICK_SEARCH_MATCH_CAP),
        },
      ];
    });
  }, [sections, trimmed, server]);
  useTrackSearch(query, () => ({
    context: AnalyticsContext.Issue,
    event: IssueEvent.QuickFilterSearch,
    data: { resultCount: results.reduce((n, match) => n + match.options.length + match.hidden, 0) },
  }));

  const update = (next: string) => {
    setQuery(next);
    onQueryChange(next);
  };

  return (
    <Command
      // The highlight must not rewrite the query — a filter search is a
      // multi-press surface, and the typed text is what the next press
      // filters against.
      mode="none"
      autoHighlight={false}
      value={query}
      onValueChange={update}
    >
      {/* The panel's search rung: the same framed well the Fields page heads
          with (and the toolbar menu's own filter field), on the panel's 24px
          content insets, with the hairline under it where the header ends and
          the list begins. It rides `OptionsPanelHeaderRung` so it stays above
          the scrolling body inside the Customize view panel; in the mobile
          filter drawer (no shell) it renders in place, under the tab strip.
          Base UI wires the input to the list through context, so the two
          living in different DOM regions costs nothing. */}
      <OptionsPanelHeaderRung>
        <Box paddingX="lg" paddingY="base">
          <AutocompleteInput
            aria-label="Filter by anything"
            placeholder="Filter by anything…"
            showClear
            clearTooltip="Clear"
            onKeyDown={(event) => {
              if (event.key !== "Escape" || !query) return;
              // The panel's own Escape closes the drawer — while a query
              // stands, Escape belongs to the query.
              event.preventDefault();
              event.stopPropagation();
              update("");
            }}
          />
        </Box>
        <Separator />
      </OptionsPanelHeaderRung>
      {trimmed && results.length === 0 ? (
        <Box paddingX="lg" paddingY="md">
          <Text size="sm" variant="muted">
            {`No filter values match “${query.trim()}”.`}
          </Text>
        </Box>
      ) : null}
      {/* The rows live inside the list Base UI registers them against —
          keyboard navigation and the listbox roles come from it. Uncapped: the
          panel is the scroller here, so the results push the body rather than
          opening a second scroll region inside it. */}
      {trimmed && results.length > 0 ? (
        <CommandList>
          {results.map(({ section, options, hidden }) => (
            <CommandGroup key={section.def.field}>
              <CommandGroupLabel>{section.def.label}</CommandGroupLabel>
              {options.map((option) => (
                <SearchResultRow
                  key={`${section.def.field}:${option.key}`}
                  section={section}
                  option={option}
                  layer={layerFor(section.def.field)}
                />
              ))}
              {hidden > 0 ? (
                <Box paddingX="sm" paddingY="2xs">
                  <Text size="xs" variant="faint">
                    {`${hidden} more — keep typing to narrow`}
                  </Text>
                </Box>
              ) : null}
            </CommandGroup>
          ))}
        </CommandList>
      ) : null}
    </Command>
  );
}

/**
 * One match. Pressing toggles the value on its section without closing the
 * list, and the row is the same one the section's own popover renders — a
 * member found here looks like the member found there.
 *
 * The group rides inline rather than heading a run: this list's group headings
 * name the FILTER a match belongs to, so a stage's workflow has nowhere else
 * to go.
 */
function SearchResultRow({
  section,
  option,
  layer,
}: {
  section: QuickFilterSection;
  option: QuickFilterOption;
  layer: UseFiltersResult;
}) {
  const field = section.def.field;
  const value = layer.quickValues[field];

  return (
    <QuickOptionRow
      value={`${field}:${option.key}`}
      option={option}
      selected={option.selected(value)}
      onToggle={() => writeQuickValue(layer, section, option.toggle(value))}
      showGroup
    />
  );
}
